import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';

import { ValidationProvider } from 'vee-validate';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import extend from 'lodash/extend';

import numeral from 'numeral';
import './formats/gr';
import '../../validations/currency';
import '../../validations/amount';

import { DEFAULT_CURRENCY_FORMAT_PATTERN } from '../../constants/currency';
import { formatCurrency } from '../../helpers';

@Component({
    data() {
        return {
            number: null,
            inputNumber: null
        };
    },
    components: {
        ValidationProvider
    }
})
export default class CurrencyAmount extends Vue {
    @Ref() readonly provider!: InstanceType<typeof ValidationProvider>;

    number;
    inputNumber;
    numeral;

    @Prop({ default: 'currency-amount-input' })
    id: string;

    @Prop()
    locale: string;

    @Prop()
    formatPattern: string;

    @Prop({ default: '' })
    label: string;

    @Prop({ default: 'currencyAmount' })
    fieldName: string;

    @Prop()
    value: number;

    @Prop([Object, String])
    rules;

    @Prop()
    disabled: boolean;

    @Prop()
    readonly: boolean;

    @Prop()
    prefix: string;

    @Prop()
    suffix: string;

    @Prop({ default: 13 })
    maxlength: number;

    @Prop(Boolean)
    enableInputFormat: boolean;

    mounted() {
        if (this.locale && numeral.locales[this.locale]) {
            numeral.locale(this.locale);
        }

        this.numeral = numeral(this.value);

        if (this.value) {
            this.number = this.numeral.value();
        }

        this.inputNumber = formatCurrency(this.number, this.locale, this.currencyFormatPattern);
        this.$emit('input', this.number);
    }

    get currencyRules() {
        const currencyRule = `currency:${this.locale}|amount:${this.number}`;

        if (isString(this.rules)) {
            const rules = this.rules.split('|');

            rules.push(currencyRule);

            return rules.join('|');
        }

        if (isObject(this.rules)) {
            return extend({
                'currency': this.locale
            }, this.rules);
        }

        return currencyRule;
    }

    get currencyFormatPattern() {
        return this.formatPattern || DEFAULT_CURRENCY_FORMAT_PATTERN;
    }

    @Watch('inputNumber')
    onChangeNumber(value, oldValue) {
        if (value === oldValue) {
            return;
        }

        this.numeral = numeral(value);
        this.number = this.numeral.value();

        if (this.enableInputFormat) {
            this.formatInput();
        }

        this.$emit('input', this.number);
    }

    formatInput() {
        if (!this.number || !this.inputNumber) {
            return;
        }

        this.inputNumber = formatCurrency(this.inputNumber, this.locale, this.currencyFormatPattern);
    }

    unsetFormat() {
        if (!this.number) {
            return;
        }

        this.inputNumber = this.number
            .toString()
            .replace('.', numeral.localeData()?.delimiters?.decimal);
    }

    @Watch('locale', { deep: true })
    onChangeLocale() {
        numeral.locale(this.locale.toLowerCase());

        this.inputNumber = formatCurrency(this.number, this.locale, this.currencyFormatPattern);
    }
}
